import React from 'react';
import { t, Trans } from '@lingui/macro';

import FrequentlyAskedQuestions from '@rover/components/src/FrequentlyAskedQuestions';
import { Paragraph } from '@rover/kibble/core';
import FirstFAQIsOpen from '@rover/react-lib/src/pages/landing/SeoLocation/components/Footer/ServiceFaqs/FirstFAQIsOpen';
import FrequentlyAskedQuestion from '@rover/react-lib/src/pages/landing/SeoLocation/components/Footer/ServiceFaqs/FrequentlyAskedQuestion';

interface DogBoardingServiceFaqsProps {
  htmlRef?: React.RefObject<HTMLElement>;
}

const DogBoardingServiceFaqs: React.FC<DogBoardingServiceFaqsProps> = ({ htmlRef }) => {
  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="dog-boarding-service-faqs"
      width="100%"
      // override defaults in FrequentlyAskedQuestions
      mt="0x"
      pb="0x"
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>Dog Boarding FAQs</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>Frequently asked questions about Rover</Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        <FrequentlyAskedQuestion question={t`What is dog boarding?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Dog boarding is overnight care for your dog in a local sitter's home while you are
              away. Boarding in a familiar home environment can help dogs feel more at ease and
              offer lots of care and attention personalised to their unique needs.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 2 */}
        <FrequentlyAskedQuestion question={t`What to know before you board your dog?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Before you board your dog, look for a sitter you trust, ensure your dog is up to date
              on vaccinations, and try a trial day to see how your dog acclimates to their new
              environment. Consult our dog boarding checklist for tips on what to pack and how to
              prepare.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 3 */}
        <FrequentlyAskedQuestion question={t`Which dog boarding options offer a Meet & Greet?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              All sitters offering dog boarding services can also offer Meet & Greets before
              booking. Message sitters you're interested in and ask if they'd like to meet up and
              discuss your dog's needs, review care instructions, and see if the environment is a
              good fit. We highly recommend having a Meet & Greet before booking a new service or
              sitter to build trust and peace of mind.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 4 */}
        <FrequentlyAskedQuestion question={t`Will someone stay with my dog 24/7?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Some sitters may offer round-the-clock (24/7) dog care. Sometimes called constant
              care, sitters who offer this service are great options for puppies, older dogs, pets
              with medical needs, or dogs with separation anxiety. Not all sitters include this in
              their listed rate, so mention &quot;24/7 care&quot; when you reach out to a sitter
              about their availability and experience.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 5 */}
        <FrequentlyAskedQuestion question={t`Are there luxury dog boarding experiences available?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Yes, in-home boarding with premium features is offered by sitters on Rover who aim to
              provide a luxury experience for dogs. Premium features can include things like
              allowing dogs on beds, playtime in fenced yards, or special comforts personalized to
              each dog's unique needs. Compared to traditional kennels, overnight boarding in a
              sitter's home is often more private and peaceful. Use search filters to find sitters
              who can meet your dog's care preferences.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 6 */}
        <FrequentlyAskedQuestion question={t`Does dog boarding include playtime and activities?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Sitters may often include playtime, walks, and activities as part of their boarding
              services. You can request specific routines to keep your dog active and happy. Some
              sitters may board multiple dogs or have day care services which can benefit your dog's
              social interaction needs. Ask about the other dogs to ensure compatibility.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 7 */}
        <FrequentlyAskedQuestion
          question={t`Which dog boarding options specialise in certain breeds or breed sizes`}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Dog boarding is available for all dog breeds and sizes. When you complete your dog's
              profile, their size will be factored into your sitter search. Some sitters will also
              specify in their profile that they enjoy working with particular breeds. Meet & Greets
              are another opportunity to further assess the security of your sitter's environment
              based on your dog's needs.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 8 */}
        <FrequentlyAskedQuestion
          question={t`What are my boarding options if my dog has special needs?`}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Many sitters note experience with special needs care, including administering
              medication, offering solo boarding, or caring for senior dogs. A good dog sitter will
              also provide cute photos and care updates to give you peace of mind. Read sitter
              profiles for their qualifications and experience.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 9 */}
        <FrequentlyAskedQuestion question={t`Is it okay to board my puppy?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Boarding is generally safe for puppies four months and older. For puppies who have
              health concerns or still need training, you may want to consider dog boarding with
              24/7 care, boarding with day care availability, or house sitting. These features will
              help your puppy maintain their play and care needs.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 10 */}
        <FrequentlyAskedQuestion question={t`Do dogs feel abandoned when boarding?`}>
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Most dogs may miss their owners but do not feel truly abandoned when given attention
              and care. For sensitive pets, try a short or trial stay first. A familiar sitter can
              help your dog feel more comfortable.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 11 */}
        <FrequentlyAskedQuestion
          question={t`Are there discounts for extended or long-term dog boarding stays?`}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Some sitters may offer discounts for longer stays or repeat bookings. Check profiles
              or message sitters directly to learn more about their pricing.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default DogBoardingServiceFaqs;
